// The list view — one report model, on screen, with the buttons that export it.
//
// Rendering and the export buttons are ui/shared/ReportView + ReportActions, so
// this file is only the dialog and the read behind it. The Installed Devices
// page uses the same two, which is what keeps the two screens from drifting
// into different-looking versions of the same sheet.
//
// ⚠️ IT DOES NOT WRITE ANYTHING. Everything on the User Access page is somebody
// else's access; this dialog only reads, so it can be closed at any point,
// including mid-gather — the run id below is what stops a cancelled read from
// painting itself over the next one.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  LinearProgress, Alert, IconButton, Tooltip, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useApp } from '../../../../core/state/AppContext';
import ReportView, { ReportMeta } from '../../../shared/ReportView';
import ReportActions from '../../../shared/ReportActions';

/**
 * `request` is { build } — `build(onProgress)` returns the report model,
 * synchronously for the scopes the page already holds in state and as a promise
 * for the ones that have to be read. null closes the dialog.
 *
 * The read is keyed on the OBJECT'S IDENTITY, not on anything inside it: the
 * caller hands over a fresh object per click, so pressing the same button twice
 * re-reads instead of re-showing a sheet that may be minutes old.
 */
export default function AccessListDialog({ request, onClose }) {
  const { companyName } = useApp();
  const [report, setReport] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');
  const runId = useRef(0);

  useEffect(() => {
    if (!request) { setReport(null); setProgress(null); setError(''); return undefined; }
    runId.current += 1;
    const mine = runId.current;
    let alive = true;
    setReport(null);
    setError('');
    setProgress({ label: 'Reading…' });

    Promise.resolve()
      .then(() => request.build((p) => {
        // A stale gather still fires its progress callbacks — it has no way to
        // know it was cancelled — so both the progress line and the result are
        // gated on the run id, not just on `alive`.
        if (alive && runId.current === mine) setProgress(p);
      }))
      .then((r) => {
        if (!alive || runId.current !== mine) return;
        setReport(r);
        setProgress(null);
      })
      .catch(() => {
        if (!alive || runId.current !== mine) return;
        setError('That list could not be read. Nothing has been changed.');
        setProgress(null);
      });

    return () => { alive = false; };
  }, [request]);

  // The letterhead is a session fact, not part of the report — core builds the
  // model and knows nothing about who is signed in.
  const sheet = useMemo(
    () => (report ? { ...report, brand: companyName || undefined } : null),
    [report, companyName],
  );

  const pct = progress && progress.total
    ? Math.round((progress.done / progress.total) * 100)
    : null;

  return (
    <Dialog
      open={!!request}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: '88vh' } }}
    >
      <DialogTitle sx={{ py: 1, pr: 6 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
          {sheet ? sheet.title : 'List'}
        </Typography>
        {sheet && <Box sx={{ mt: 0.5 }}><ReportMeta report={sheet} /></Box>}
        <Tooltip title="Close">
          <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', top: 8, right: 8 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ py: 1 }}>
        {progress && (
          <Box sx={{ py: 2 }}>
            <Typography sx={{ fontSize: 12, mb: 0.75 }}>
              {progress.label || 'Reading…'}
            </Typography>
            <LinearProgress
              variant={pct == null ? 'indeterminate' : 'determinate'}
              value={pct == null ? undefined : pct}
            />
            <Typography color="text.secondary" sx={{ fontSize: 10.5, mt: 0.75 }}>
              Every room is read the same way the panels read it, so the list
              matches the screen. On a large site that is a request per room.
            </Typography>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>}

        <ReportView report={sheet} />
      </DialogContent>

      <Divider />
      <DialogActions sx={{ py: 0.75 }}>
        {sheet && (
          <Typography color="text.secondary" sx={{ fontSize: 11, mr: 'auto', ml: 1 }}>
            {`${sheet.total} device${sheet.total === 1 ? '' : 's'} in total`}
          </Typography>
        )}
        <Button onClick={onClose}>Close</Button>
        <ReportActions report={sheet} onError={setError} />
      </DialogActions>
    </Dialog>
  );
}
